import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DownloadOutlined,
  FileOutlined,
  HeartOutlined,
  InfoCircleOutlined,
  PaperClipOutlined,
  SendOutlined,
  SmileOutlined,
} from "@ant-design/icons";
import { Button, Image, Input, Popover, Spin, Tooltip, message } from "antd";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useSelector } from "react-redux";
import { RootState } from "../../../../stores/Store";
import {
  makeTempMessageId,
  removeGroupMessage,
  useGroupMessages,
  useMarkGroupRead,
  useSendGroupAttachment,
  useSendGroupMessage,
} from "../../../../hooks/useChatGroup";
import { ChatAttachmentDto } from "../../../../models/chat/ChatMessageDto";
import { ChatGroupMessageDto } from "../../../../models/chat/ChatGroup";
import { CHAT_EMOJIS, CHAT_STICKERS, getStickerEmoji } from "../../../../utils/chatStickers";
import { linkify } from "../../../../utils/linkify";
import { chatGroupService } from "../../../../services/ChatGroupService";
import { CommonAvatar } from "../avatar/CommonAvatar";
import { getProfileName, useChatUserProfiles } from "./useChatUserProfiles";
import { GroupInfoModal } from "./GroupInfoModal";

const MAX_ATTACHMENT_SIZE = 50 * 1024 * 1024;
const CHAT_POPUP_Z_INDEX = 1600;

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

interface GroupChatWindowProps {
  groupId: string;
  groupName?: string;
  avatarUrl?: string | null;
  minimized: boolean;
  onClose: () => void;
  onToggleMinimize: () => void;
  onHeaderPointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void;
}

export const GroupChatWindow = memo(
  ({ groupId, groupName, avatarUrl, minimized, onClose, onToggleMinimize, onHeaderPointerDown }: GroupChatWindowProps) => {
    const myUserId = useSelector((state: RootState) => state.auth.userId);
    const [content, setContent] = useState("");
    const [stickerOpen, setStickerOpen] = useState(false);
    const [emojiOpen, setEmojiOpen] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const preserveTopMessageIdRef = useRef<string | null>(null);

    const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useGroupMessages(groupId, !minimized);
    const sendMessage = useSendGroupMessage();
    const sendAttachment = useSendGroupAttachment();
    const markRead = useMarkGroupRead();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const attachmentFilesRef = useRef<Map<string, File[]>>(new Map());

    const messages = useMemo(() => data?.pages.flat() ?? [], [data]);
    const ordered = useMemo(() => [...messages].reverse(), [messages]);
    const latestId = messages[0]?.id;
    const messageVirtualizer = useVirtualizer({
      count: ordered.length,
      getScrollElement: () => containerRef.current,
      estimateSize: (index) => {
        const m = ordered[index];
        if (!m) return 74;
        if (m.messageType === "SYSTEM") return 44;
        if (m.messageType === "ATTACHMENT") return 178;
        if (m.messageType === "STICKER") return 88;
        return 66;
      },
      getItemKey: (index) => ordered[index]?.id ?? index,
      overscan: 8,
      scrollingDelay: 80,
    });
    const virtualMessages = messageVirtualizer.getVirtualItems();
    const totalMessageHeight = messageVirtualizer.getTotalSize();

    const profileUserIds = useMemo(() => {
      const ids = new Set<string>();
      messages.forEach((m) => {
        ids.add(m.senderUserId);
        m.seenByUserIds?.forEach((id) => ids.add(id));
      });
      if (myUserId) ids.add(myUserId);
      return Array.from(ids);
    }, [messages, myUserId]);
    const profileByUserId = useChatUserProfiles(profileUserIds);

    const lastSeenMessageId = messages.find((m) => m.senderUserId === myUserId && (m.seenByUserIds?.length ?? 0) > 0)?.id;

    useEffect(() => {
      if (!minimized) markRead.mutate(groupId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minimized, groupId]);

    useEffect(() => {
      if (minimized || ordered.length === 0 || preserveTopMessageIdRef.current) return;
      const frame = window.requestAnimationFrame(() => {
        messageVirtualizer.scrollToIndex(ordered.length - 1, { align: "end" });
      });
      return () => window.cancelAnimationFrame(frame);
    }, [latestId, minimized, isLoading, ordered.length, messageVirtualizer]);

    useEffect(() => {
      const preservedMessageId = preserveTopMessageIdRef.current;
      if (!preservedMessageId) return;
      const preservedIndex = ordered.findIndex((message) => message.id === preservedMessageId);
      if (preservedIndex < 0) return;
      const frame = window.requestAnimationFrame(() => {
        messageVirtualizer.scrollToIndex(preservedIndex, { align: "start" });
        preserveTopMessageIdRef.current = null;
      });
      return () => window.cancelAnimationFrame(frame);
    }, [ordered, messageVirtualizer]);

    const onScroll = useCallback(() => {
      const el = containerRef.current;
      if (!el || el.scrollTop > 40 || !hasNextPage || isFetchingNextPage) return;
      const firstVirtualIndex = messageVirtualizer.getVirtualItems()[0]?.index ?? 0;
      preserveTopMessageIdRef.current = ordered[firstVirtualIndex]?.id ?? ordered[0]?.id ?? null;
      fetchNextPage();
    }, [fetchNextPage, hasNextPage, isFetchingNextPage, messageVirtualizer, ordered]);

    const send = () => {
      const text = content.trim();
      if (!text) return;
      sendMessage.mutate({ groupId, content: text, tempId: makeTempMessageId() });
      setContent("");
    };

    const pickFiles = () => fileInputRef.current?.click();

    const onFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = "";
      if (!files.length) return;
      if (files.some((f) => f.size > MAX_ATTACHMENT_SIZE)) {
        message.error("Mỗi tệp tối đa 50MB");
        return;
      }
      sendFiles(files, content.trim() || undefined);
      setContent("");
    };

    const sendFiles = (files: File[], caption?: string) => {
      const tempId = makeTempMessageId();
      attachmentFilesRef.current.set(tempId, files);
      sendAttachment.mutate({ groupId, files, content: caption, tempId }, { onSuccess: () => attachmentFilesRef.current.delete(tempId) });
    };

    const sendSticker = (code: string) => {
      setStickerOpen(false);
      sendMessage.mutate({ groupId, content: code, messageType: "STICKER", tempId: makeTempMessageId() });
    };

    const retry = (m: ChatGroupMessageDto) => {
      if (m.messageType === "ATTACHMENT") {
        const files = attachmentFilesRef.current.get(m.id);
        attachmentFilesRef.current.delete(m.id);
        removeGroupMessage(groupId, m.id);
        if (!files || files.length === 0) {
          message.info("Vui lòng chọn lại tệp để gửi");
          return;
        }
        sendFiles(files, m.content || undefined);
        return;
      }
      removeGroupMessage(groupId, m.id);
      sendMessage.mutate({ groupId, content: m.content, messageType: m.messageType, tempId: makeTempMessageId() });
    };

    const insertEmoji = (emoji: string) => {
      setContent((prev) => (prev.length >= 2000 ? prev : prev + emoji));
    };

    const renderAttachments = (m: ChatGroupMessageDto, mine: boolean) => {
      const atts: ChatAttachmentDto[] = m.attachments ?? [];
      const images = atts.filter((a) => a.isImage && a.url);
      const files = atts.filter((a) => !a.isImage);
      const sending = m.status === "sending";
      return (
        <div className="flex flex-col gap-1">
          {images.length > 0 && (
            <Image.PreviewGroup>
              <div className="flex flex-wrap gap-1">
                {images.map((a) => (
                  <Image
                    key={a.id}
                    src={a.url ?? undefined}
                    alt={a.originalFileName}
                    width={132}
                    height={132}
                    className="rounded-[14px]"
                    style={{ objectFit: "cover" }}
                    preview={!sending}
                  />
                ))}
              </div>
            </Image.PreviewGroup>
          )}
          {files.map((a) => (
            <button
              key={a.id}
              type="button"
              disabled={sending}
              onClick={() => chatGroupService.downloadGroupFile(groupId, a.id, a.originalFileName)}
              className={[
                "flex max-w-[260px] cursor-pointer items-center gap-2 rounded-[18px] border px-3 py-2 text-sm shadow-sm disabled:cursor-default disabled:opacity-70",
                mine ? "border-[#b9d7ff] bg-white/95 text-[#10243f] hover:bg-white" : "border-white bg-white text-neutral-800 hover:bg-neutral-50",
              ].join(" ")}
            >
              <FileOutlined className="shrink-0 text-blue-500" />
              <span className="truncate flex-1 text-left">{a.originalFileName}</span>
              <span className="shrink-0 text-[11px] text-neutral-400">{formatSize(a.fileSize)}</span>
              {!sending && <DownloadOutlined className="shrink-0 text-neutral-400" />}
            </button>
          ))}
          {m.content && (
            <div
              className={[
                "rounded-[18px] px-3 py-1.5 text-sm leading-5 whitespace-pre-wrap break-words shadow-sm",
                mine ? "bg-[#d9ecff] text-[#10243f]" : "bg-white text-[#17212f]",
              ].join(" ")}
            >
              {linkify(m.content)}
            </div>
          )}
        </div>
      );
    };

    const emojiPicker = (
      <div className="grid grid-cols-8 gap-0.5 w-64">
        {CHAT_EMOJIS.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => insertEmoji(e)}
            className="text-xl leading-none p-1 rounded bg-transparent border-none cursor-pointer hover:bg-neutral-100 transition"
          >
            {e}
          </button>
        ))}
      </div>
    );

    const stickerPicker = (
      <div className="grid grid-cols-4 gap-1 w-52">
        {CHAT_STICKERS.map((s) => (
          <button
            key={s.code}
            type="button"
            title={s.label}
            onClick={() => sendSticker(s.code)}
            className="text-2xl leading-none p-1.5 rounded-lg bg-transparent border-none cursor-pointer hover:bg-neutral-100 transition"
          >
            {s.emoji}
          </button>
        ))}
      </div>
    );

    const header = (
      <div
        className="flex items-center justify-between gap-2 rounded-t-[18px] bg-[#0068ff] px-3.5 py-2.5 text-white shadow-[0_2px_12px_rgba(0,104,255,0.24)] cursor-pointer select-none"
        style={{ touchAction: "none" }}
        onPointerDown={onHeaderPointerDown}
        onClick={onToggleMinimize}
      >
        <div className="flex items-center gap-2 min-w-0">
          <CommonAvatar size={34} src={avatarUrl ?? undefined} name={groupName} className="bg-white text-[#0068ff] shrink-0" />
          <div className="min-w-0 leading-tight">
            <span className="font-medium text-sm truncate block">{groupName || "Nhóm"}</span>
            <span className="text-[11px] text-white/80">Nhóm chat</span>
          </div>
        </div>
        <div className="flex items-center shrink-0">
          <button
            type="button"
            title="Thông tin nhóm"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-none bg-white/10 p-0 text-white/80 hover:bg-white/20 hover:text-white"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              setInfoOpen(true);
            }}
          >
            <InfoCircleOutlined />
          </button>
        </div>
      </div>
    );

    if (minimized) {
      return <div className="w-72 max-w-[calc(100vw-16px)] rounded-[18px] shadow-lg">{header}</div>;
    }

    return (
      <div className="flex h-[560px] max-h-[calc(100vh-28px)] w-[380px] max-w-[calc(100vw-16px)] transform-gpu flex-col overflow-hidden rounded-[18px] border border-[#d9e4f2] bg-white shadow-[0_18px_48px_rgba(15,23,42,0.22)] sm:max-h-[76vh]">
        {header}

        <div ref={containerRef} onScroll={onScroll} className="flex-1 overflow-y-auto bg-[#eaf1fb] px-3.5 py-3">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spin />
            </div>
          ) : (
            <>
              {isFetchingNextPage && (
                <div className="flex justify-center py-1">
                  <Spin size="small" />
                </div>
              )}
              <div className="relative w-full" style={{ height: totalMessageHeight }}>
                {virtualMessages.map((virtualMessage) => {
                  const index = virtualMessage.index;
                  const m = ordered[index];
                  if (!m) return null;
                  if (m.messageType === "SYSTEM") {
                    return (
                      <div
                        key={virtualMessage.key}
                        data-index={virtualMessage.index}
                        ref={(node) => {
                          if (node) messageVirtualizer.measureElement(node);
                        }}
                        className="absolute left-0 top-0 w-full"
                        style={{ transform: `translateY(${virtualMessage.start}px)` }}
                      >
                        <div className="my-3 flex justify-center">
                          <span
                            className="rounded-full bg-white/70 px-3 py-1 text-center text-[11px] text-[#6b7280] shadow-sm"
                            title={m.registeredTime}
                          >
                            {m.content}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  const mine = m.senderUserId === myUserId;
                  const isSticker = m.messageType === "STICKER";
                  const isAttachment = m.messageType === "ATTACHMENT";
                  const senderProfile = profileByUserId.get(m.senderUserId);
                  const senderName = getProfileName(senderProfile, m.senderName || m.senderUserId);
                  const seen = mine && m.id === lastSeenMessageId ? (m.seenByUserIds ?? []) : [];
                  const prev = ordered[index - 1];
                  const next = ordered[index + 1];
                  const groupedWithPrev = prev?.messageType !== "SYSTEM" && prev?.senderUserId === m.senderUserId;
                  const groupedWithNext = next?.messageType !== "SYSTEM" && next?.senderUserId === m.senderUserId;
                  const showSenderAvatar = !mine && !groupedWithNext;
                  const bubbleShape = mine
                    ? groupedWithNext
                      ? "rounded-[18px] rounded-br-md"
                      : "rounded-[18px] rounded-br-[6px]"
                    : groupedWithNext
                      ? "rounded-[18px] rounded-bl-md"
                      : "rounded-[18px] rounded-bl-[6px]";
                  return (
                    <div
                      key={virtualMessage.key}
                      data-index={virtualMessage.index}
                      ref={(node) => {
                        if (node) messageVirtualizer.measureElement(node);
                      }}
                      className="absolute left-0 top-0 w-full"
                      style={{ transform: `translateY(${virtualMessage.start}px)` }}
                    >
                      <div className={`flex items-end gap-2 ${groupedWithPrev ? "mt-1" : "mt-3"} ${mine ? "justify-end" : "justify-start"}`}>
                        {!mine && (
                          <div className="w-7 shrink-0">
                            {showSenderAvatar && (
                              <CommonAvatar size={28} src={senderProfile?.avatarId} name={senderName} className="bg-white text-[#0068ff] shadow-sm" />
                            )}
                          </div>
                        )}
                        <div
                          className={`flex max-w-[78%] flex-col ${mine ? "items-end" : "items-start"} ${m.status === "sending" ? "opacity-70" : ""}`}
                        >
                          {!mine && !groupedWithPrev && <span className="mb-1 ml-1 text-[11px] font-medium text-[#687588]">{senderName}</span>}
                          {isSticker ? (
                            <div className="select-none text-5xl leading-none drop-shadow-sm" title={m.registeredTime}>
                              {getStickerEmoji(m.content) ?? "❓"}
                            </div>
                          ) : isAttachment ? (
                            <div title={m.registeredTime}>{renderAttachments(m, mine)}</div>
                          ) : (
                            <div
                              className={`px-3 py-2 text-[14px] leading-5 whitespace-pre-wrap break-words shadow-sm ${bubbleShape} ${
                                mine ? "bg-[#d9ecff] text-[#10243f]" : "bg-white text-[#17212f]"
                              }`}
                              title={m.registeredTime}
                            >
                              {linkify(m.content)}
                            </div>
                          )}
                          {m.registeredTime && (
                            <div className={`mt-0.5 px-1 text-[10px] text-neutral-400 ${mine ? "text-right" : "text-left"}`}>{m.registeredTime}</div>
                          )}
                          {mine && m.status && (
                            <div className="text-[10px] mt-0.5 pr-1">
                              {m.status === "sending" ? (
                                <span className="text-neutral-400">Đang gửi…</span>
                              ) : (
                                <span className="text-red-500 cursor-pointer hover:underline" onClick={() => retry(m)}>
                                  Gửi lỗi · Gửi lại
                                </span>
                              )}
                            </div>
                          )}
                          {seen.length > 0 && (
                            <div className="flex items-center gap-0.5 mt-0.5 pr-1">
                              {seen.slice(0, 5).map((uid) => (
                                <Tooltip key={uid} title={getProfileName(profileByUserId.get(uid), uid)}>
                                  <span>
                                    <CommonAvatar
                                      size={14}
                                      src={profileByUserId.get(uid)?.avatarId}
                                      name={getProfileName(profileByUserId.get(uid), uid)}
                                      className="bg-slate-300 text-white !text-[8px]"
                                    />
                                  </span>
                                </Tooltip>
                              ))}
                              {seen.length > 5 && <span className="text-[10px] text-neutral-400">+{seen.length - 5}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="flex items-end gap-1.5 border-t border-[#dbe5f0] bg-white px-2.5 py-2.5">
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onFilesSelected} />
          <Tooltip title="Đính kèm tệp">
            <Button
              type="text"
              icon={<PaperClipOutlined />}
              className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !text-[#5f6b7a] shrink-0 hover:!bg-[#eef4ff] hover:!text-[#0068ff]"
              loading={sendAttachment.isPending}
              onClick={pickFiles}
            />
          </Tooltip>
          <Popover
            content={emojiPicker}
            trigger="click"
            open={emojiOpen}
            onOpenChange={(open) => {
              setEmojiOpen(open);
              if (open) setStickerOpen(false);
            }}
            placement="topLeft"
            zIndex={CHAT_POPUP_Z_INDEX}
          >
            <Tooltip title="Emoji">
              <Button
                type="text"
                icon={<SmileOutlined />}
                className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !text-[#5f6b7a] shrink-0 hover:!bg-[#eef4ff] hover:!text-[#0068ff]"
              />
            </Tooltip>
          </Popover>
          <Popover
            content={stickerPicker}
            trigger="click"
            open={stickerOpen}
            onOpenChange={(open) => {
              setStickerOpen(open);
              if (open) setEmojiOpen(false);
            }}
            placement="topLeft"
            zIndex={CHAT_POPUP_Z_INDEX}
          >
            <Tooltip title="Nhãn dán">
              <Button
                type="text"
                icon={<HeartOutlined />}
                className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !text-[#5f6b7a] shrink-0 hover:!bg-[#eef4ff] hover:!text-[#0068ff]"
              />
            </Tooltip>
          </Popover>
          <Input.TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            maxLength={2000}
            placeholder="Nhập tin nhắn..."
            className="!rounded-[20px] !border-none !bg-[#f2f4f8] !px-3 !py-2 !text-[14px] !leading-5 !shadow-none focus:!bg-white"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          <Button
            type="primary"
            shape="circle"
            icon={<SendOutlined />}
            disabled={!content.trim()}
            loading={sendMessage.isPending}
            className="!h-9 !w-9 !min-w-9 !bg-[#0068ff] !text-white"
            onClick={send}
          />
        </div>

        <GroupInfoModal open={infoOpen} groupId={groupId} groupName={groupName} onClose={() => setInfoOpen(false)} onLeft={() => onClose()} />
      </div>
    );
  },
);
